// 113 — Bitácora UCI Adulto (Fase 4, Tandas 5a+5b+8a, ver PLAN.md "FASE 4
// — spec completa" → "113 — Bitácora UCI Adulto"). Bloques A (datos del
// establecimiento/usuario, vía `EncabezadoMspCampos`/`useDatosEncabezadoMsp`
// — mismo header MSP que 051, footer real del formulario confirmado
// "SNS-MSP/HCU-form.113/2021"), B (antropometría) y C completo (bitácora:
// diagnósticos de ingreso, constantes vitales + gráfico, resultados de
// laboratorio, ingresos, egresos, reflejos miotáticos, escalas
// EVA/RASS/Ramsay/Mottling/Glasgow, gases y balance hídrico — página 220
// del PDF fuente, Tanda 5b) + D-J (ventilación mecánica, procedimientos,
// oxigenación, solicitud de exámenes, cuidados generales, interconsultas,
// informe de enfermería — página 221, Tanda 8a) + K (datos del profesional
// responsable por turno AM/PM/HS — T6, 2026-09-23: el turno que firma lo arma el servidor, los otros dos
// se capturan estructurados; ver `profesionalPorTurnoDatos.ts`).
//
// FHIR: el `Encounter` pasa a class=ACUTE + extensión de nivel de cuidado
// intensivo (mecanismo ya existente en `cuidadoIntensivo.tsx`, preservado
// acá), hasta 5 `Condition` (uno por diagnóstico de ingreso con texto, el
// papel imprime 5 filas — mismo mecanismo que "motivo de ingreso" ya
// tenía para 1 sola), y Talla/Peso crean `Observation` reusando los LOINC
// ya definidos en `vitalSigns.ts` (mismo signo vital, mismo código, sin
// inventar uno nuevo). El resto (IMC/peso ideal/superficie corporal,
// servicio/fechas/alergias/grupo-factor, resultados de laboratorio,
// constantes/ingresos/egresos/escalas/gases horarios, balance hídrico) es
// `Par[]` de respaldo — sin un consumidor clínico discreto hoy que
// justifique un recurso nuevo, mismo criterio que la Tanda 1b/2 de 051.
//
// Balance hídrico (TOTAL 1-6/SUMA/BALANCE DIARIO/BALANCE TOTAL, columna
// derecha del papel): se CALCULAN desde las filas horarias de ingresos y
// egresos (ver "balance hídrico" más abajo: qué filas componen cada total y
// qué se deja fuera). Cada campo sigue siendo editable: lo que la enfermera
// escribe a propósito sobrescribe el cálculo; vaciarlo vuelve al cálculo.
// El BALANCE TOTAL (acumulado de todos los días en UCI) no se puede derivar
// de una sola hoja y queda manual. IMC de la antropometría: calculado; peso
// ideal y superficie corporal siguen manuales (el instructivo de la pág.
// 223 del PDF no trae fórmula para ninguno de los dos).
import type { DatosGraficoConstantesUciPdf } from '@logiacare/hce-pdf';
import { balanceHidricoEfectivo, imcTexto, RANGOS_GLASGOW, totalGlasgow, type ConfigBalanceHidrico } from './calculosClinicosDatos';
import type { FilaTablaHoraria24h } from './uci/TablaHoraria24h';
import { resumenFilaTablaHoraria24h, horaValorTexto, COLUMNAS_HORARIAS_UCI, type ColumnaHorariaUci, type EstadoTablaHoraria24h } from './uci/TablaHoraria24h';
import { estadoInicialTablaHoraria24h } from './uci/TablaHoraria24h';
import { estadoInicialGraficoEnfermeria, type PuntosGraficoEnfermeria, type SerieGraficoEnfermeria } from './uci/GraficoEnfermeria';
import { estadoInicialChecklistTurno, paresDeItemsChecklistTurno, type EstadoChecklistTurno, type ItemChecklistTurno } from './uci/ChecklistTurno';
import { estadoInicialFilasNombrables, paresDeFilasNombrables, tablaConGruposNombrables, type EstadoFilasNombrables } from './uci/filasNombrables';
import { diasHospitalizacionEfectivo, estadoInicialAlergiasSangre, paresAlergiasSangre, type EstadoAlergiasSangre } from './uci/datosClinicosUci';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { CAMPOS_PROFESIONAL_113, estadoInicialProfesionalPorTurno, paresProfesionalPorTurno, type EstadoProfesionalPorTurno } from './profesionalPorTurnoDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

// ---------- B. Antropometría ----------

export interface EstadoAntropometria113 {
  readonly talla: string;
  readonly pesoReal: string;
  readonly imc: string;
  readonly pesoIdeal: string;
  readonly superficieCorporal: string;
}

export function estadoInicialAntropometria113(): EstadoAntropometria113 {
  return { talla: '', pesoReal: '', imc: '', pesoIdeal: '', superficieCorporal: '' };
}

function paresDeAntropometria113(t: Traducir, e: EstadoAntropometria113): Par[] {
  return [
    { pregunta: t('formularios.f113.antropometria.talla'), respuesta: e.talla.trim() },
    { pregunta: t('formularios.f113.antropometria.pesoReal'), respuesta: e.pesoReal.trim() },
    // IMC = peso real / talla² — calculado (solo lectura). `e.imc` queda solo por compatibilidad con el estado anterior.
    { pregunta: t('formularios.f113.antropometria.imc'), respuesta: imcTexto(e.pesoReal, e.talla) },
    { pregunta: t('formularios.f113.antropometria.pesoIdeal'), respuesta: e.pesoIdeal.trim() },
    { pregunta: t('formularios.f113.antropometria.superficieCorporal'), respuesta: e.superficieCorporal.trim() },
  ];
}

// ---------- C. Bitácora — datos generales + diagnósticos ----------

/**
 * Ola 2 (M2): alergias Sí/No + especificar, grupo sanguíneo y factor Rh separados (instructivo, pág. 223) y días de hospitalización
 * CALCULADOS desde las fechas (`diasHospitalizacion` queda solo como respaldo manual cuando falta una fecha). El "Grupo y factor" de
 * texto libre ya no se captura (lo firmado antes conserva su par viejo).
 */
export interface EstadoDatosBitacora113 extends EstadoAlergiasSangre {
  readonly servicioProcedencia: string;
  readonly fechaIngresoUci: string;
  readonly fechaActual: string;
  /** Solo se usa si las fechas no permiten calcularlo. */
  readonly diasHospitalizacion: string;
}

export function estadoInicialDatosBitacora113(): EstadoDatosBitacora113 {
  return { servicioProcedencia: '', fechaIngresoUci: '', fechaActual: '', diasHospitalizacion: '', ...estadoInicialAlergiasSangre() };
}

function paresDeDatosBitacora113(t: Traducir, e: EstadoDatosBitacora113): Par[] {
  return [
    { pregunta: t('formularios.f113.datosBitacora.servicioProcedencia'), respuesta: e.servicioProcedencia.trim() },
    { pregunta: t('formularios.f113.datosBitacora.fechaIngresoUci'), respuesta: e.fechaIngresoUci.trim() },
    { pregunta: t('formularios.f113.datosBitacora.fechaActual'), respuesta: e.fechaActual.trim() },
    { pregunta: t('formularios.f113.datosBitacora.diasHospitalizacion'), respuesta: diasHospitalizacionEfectivo(e.fechaIngresoUci, e.fechaActual, e.diasHospitalizacion) },
    ...paresAlergiasSangre(t, t('formularios.f113.datosBitacora.alergias'), e),
  ];
}

/** 5 filas en el papel — mismo mecanismo que "motivo de ingreso" de `cuidadoIntensivo.tsx`, extendido de 1 a 5. */
export const NUM_DIAGNOSTICOS_INGRESO_113 = 5;

export function estadoInicialDiagnosticos113(): readonly DiagnosticoCie10Valor[] {
  return Array.from({ length: NUM_DIAGNOSTICOS_INGRESO_113 }, () => ({ texto: '', cie10: null }));
}

function paresDeDiagnosticos113(t: Traducir, diagnosticos: readonly DiagnosticoCie10Valor[]): Par[] {
  return diagnosticos
    .map((d, i) => ({ pregunta: t(`formularios.f113.diagnosticosIngreso.${i + 1}`), respuesta: conCodigoCie(d) }))
    .filter((p) => p.respuesta !== '');
}

// ---------- C. Bitácora — constantes vitales (TablaHoraria24h, sin gráfico) ----------

export const FILAS_CONSTANTES_VITALES_113: readonly FilaTablaHoraria24h[] = [
  { clave: 'pvc', etiqueta: 'Presión venosa central' },
  { clave: 'tam', etiqueta: 'Tensión arterial media' },
  { clave: 'gastoCardiaco', etiqueta: 'Gasto cardíaco' },
  { clave: 'indiceCardiaco', etiqueta: 'Índice cardíaco' },
  { clave: 'volumenSistolico', etiqueta: 'Volumen sistólico' },
  { clave: 'rvsIndexada', etiqueta: 'Resistencia vascular sistémica indexada' },
  { clave: 'presionIntraAbdominal', etiqueta: 'Presión intra-abdominal' },
  { clave: 'pulsioximetria', etiqueta: 'Pulsioximetría' },
];

// ---------- C. Bitácora — gráfico de constantes (FR/T/Pulso/TA superpuestos) ----------

/**
 * 4 series confirmadas leyendo la página 220 del PDF fuente: FR (20-90,
 * símbolo por modo ventilatorio — ○ espontánea/asistida, ● controlada),
 * Temperatura (33-41, triángulo), Pulso (20-180, círculo relleno), Tensión
 * arterial (40-200, cruz). Mismos hex que la paleta pine/clay/sage de
 * `signosVitalesTimeline.ts`/`partograma.ts` en `hce-pdf`, en el mismo
 * orden — así el color de cada serie coincide entre la pantalla y el PDF
 * firmado.
 */
export const SERIES_GRAFICO_CONSTANTES_113: readonly SerieGraficoEnfermeria[] = [
  {
    clave: 'fr',
    etiqueta: 'Frecuencia respiratoria',
    simbolo: 'circuloHueco',
    color: '#1f4d47',
    escala: { min: 20, max: 90 },
    variantes: [
      { clave: 'espontanea', etiqueta: 'Espontánea', simbolo: 'circuloHueco' },
      { clave: 'asistida', etiqueta: 'Asistida', simbolo: 'circuloHueco' },
      { clave: 'controlada', etiqueta: 'Controlada', simbolo: 'circulo' },
    ],
  },
  { clave: 'temperatura', etiqueta: 'Temperatura', simbolo: 'triangulo', color: '#b5563c', escala: { min: 33, max: 41 } },
  { clave: 'pulso', etiqueta: 'Pulso', simbolo: 'circulo', color: '#6f8f6a', escala: { min: 20, max: 180 } },
  { clave: 'tensionArterial', etiqueta: 'Tensión arterial', simbolo: 'cruz', color: '#153733', escala: { min: 40, max: 200 } },
];

/** Serializa el estado del gráfico de constantes a `DatosGraficoConstantesUciPdf` (wire format de `hce-pdf`) — pura, exportada para test. Dispersa: solo las horas con punto cargado por serie. */
export function diagramaGraficoConstantesUciDeEstado(puntos: PuntosGraficoEnfermeria): DatosGraficoConstantesUciPdf {
  return {
    series: SERIES_GRAFICO_CONSTANTES_113.map((serie) => ({
      clave: serie.clave,
      etiqueta: serie.etiqueta,
      simbolo: serie.simbolo,
      escalaMin: serie.escala.min,
      escalaMax: serie.escala.max,
      variantes: serie.variantes?.map((v) => ({ clave: v.clave, simbolo: v.simbolo })),
      puntos: COLUMNAS_HORARIAS_UCI.filter((c) => puntos[serie.clave]?.[c] !== undefined).map((c) => ({
        columna: c,
        valor: puntos[serie.clave]![c]!.valor,
        variante: puntos[serie.clave]![c]!.variante,
      })),
    })),
  };
}

// ---------- C. Bitácora — renglones nombrables (medicación, infusiones, drenajes, "otros") ----------
// Ola 2 (M2): el papel deja ~12 renglones para medicación (2), ~15 para infusiones (3), ~5 para drenajes y 4 en blanco al final de constantes
// vitales y de ventilación (21-24) — conteo aproximado sobre el escaneo. Cada renglón lleva NOMBRE + 24 horas (`uci/filasNombrables.ts`), con
// tope de 20 (8 en drenajes, 4 en "otros"). Las tres primeras suman a los TOTAL 2, 3 y 5 del balance con las claves sintéticas
// `medicacion`, `infusiones` y `drenajes` (las mismas que usaba la fila única anterior).

export const TOPE_DRENAJES_113 = 8;
export const TOPE_OTROS_HORARIOS_113 = 4;

export interface GruposNombrables113 {
  readonly medicacion: EstadoFilasNombrables;
  readonly infusiones: EstadoFilasNombrables;
  readonly drenajes: EstadoFilasNombrables;
  readonly otrasConstantes: EstadoFilasNombrables;
  readonly otrosVentilacion: EstadoFilasNombrables;
}

export function estadoInicialGruposNombrables113(): GruposNombrables113 {
  return {
    medicacion: estadoInicialFilasNombrables(4),
    infusiones: estadoInicialFilasNombrables(4),
    drenajes: estadoInicialFilasNombrables(2),
    otrasConstantes: estadoInicialFilasNombrables(1),
    otrosVentilacion: estadoInicialFilasNombrables(1),
  };
}

// ---------- C. Bitácora — ingresos ----------

export const FILAS_INGRESOS_113: readonly FilaTablaHoraria24h[] = [
  { clave: 'aguaEndogena', etiqueta: 'Agua endógena', unidad: 'mL' },
  { clave: 'liquidosViaOral', etiqueta: 'Líquidos vía oral', unidad: 'mL' },
  { clave: 'dietaHoraria', etiqueta: 'Dieta — horaria', unidad: 'mL' },
  { clave: 'dietaTotal', etiqueta: 'Dieta — total', unidad: 'mL' },
];

// ---------- C. Bitácora — egresos ----------

export const FILAS_EGRESOS_113: readonly FilaTablaHoraria24h[] = [
  { clave: 'insensibles', etiqueta: 'Insensibles', unidad: 'mL' },
  { clave: 'vomito', etiqueta: 'Vómito', unidad: 'mL' },
  { clave: 'sondaNasogastrica', etiqueta: 'Sonda nasogástrica', unidad: 'mL' },
  { clave: 'heces', etiqueta: 'Heces' },
  { clave: 'irrigacionEntrada', etiqueta: 'Irrigación — entrada', unidad: 'mL' },
  { clave: 'irrigacionSalida', etiqueta: 'Irrigación — salida', unidad: 'mL' },
  { clave: 'diuresisHoraria', etiqueta: 'Diuresis — horaria', unidad: 'mL' },
  { clave: 'diuresisTotal', etiqueta: 'Diuresis — total', unidad: 'mL' },
];

// ---------- C. Bitácora — reflejos miotáticos ----------

export const FILAS_REFLEJOS_113: readonly FilaTablaHoraria24h[] = [{ clave: 'reflejosMiotaticos', etiqueta: 'Reflejos miotáticos' }];

// ---------- C. Bitácora — escalas (EVA/RASS/Ramsay/Mottling/pupilas/Glasgow) ----------

export const FILAS_ESCALAS_113: readonly FilaTablaHoraria24h[] = [
  { clave: 'eva', etiqueta: 'Escala del dolor EVA (1-10)' },
  { clave: 'rass', etiqueta: 'RASS' },
  { clave: 'ramsay', etiqueta: 'Ramsay' },
  { clave: 'mottling', etiqueta: 'Mottling Score' },
  { clave: 'pupilaDerecha', etiqueta: 'Diámetro pupilar — D' },
  { clave: 'pupilaIzquierda', etiqueta: 'Diámetro pupilar — I' },
  { clave: 'aperturaOcular', etiqueta: 'Apertura ocular (4)', rango: RANGOS_GLASGOW.ocular },
  { clave: 'respuestaMotora', etiqueta: 'Respuesta motora (6)', rango: RANGOS_GLASGOW.motor },
  { clave: 'respuestaVerbal', etiqueta: 'Respuesta verbal (5)', rango: RANGOS_GLASGOW.verbal },
];

/**
 * Total Glasgow (apertura ocular + respuesta motora + respuesta verbal) por
 * hora — aritmética médica estándar (3-15). `undefined` en las horas donde
 * falte cualquiera de los 3 componentes, no sea numérico o esté fuera de su
 * rango (ocular 1-4, verbal 1-5, motora 1-6) — mismo criterio que
 * `calcularTotalEscala` de `EscalaClinica.tsx` (nunca un total parcial
 * inventado). Pura, exportada para test.
 */
export function totalGlasgowPorHora(escalas: EstadoTablaHoraria24h): Partial<Record<ColumnaHorariaUci, number>> {
  const total: Partial<Record<ColumnaHorariaUci, number>> = {};
  for (const columna of COLUMNAS_HORARIAS_UCI) {
    const suma = totalGlasgow(escalas.aperturaOcular?.[columna], escalas.respuestaVerbal?.[columna], escalas.respuestaMotora?.[columna]);
    if (suma !== null) total[columna] = suma;
  }
  return total;
}

/** Resumen compacto "10 h: total · 11 h: total" del Glasgow calculado — mismo formato que `resumenFilaTablaHoraria24h`. */
export function resumenTotalGlasgowPorHora(escalas: EstadoTablaHoraria24h): string {
  const total = totalGlasgowPorHora(escalas);
  return COLUMNAS_HORARIAS_UCI.filter((c) => total[c] !== undefined)
    .map((c) => horaValorTexto(c, String(total[c])))
    .join(' · ');
}

// ---------- C. Bitácora — gases + glucosa ----------

export const FILAS_GASES_113: readonly FilaTablaHoraria24h[] = [
  { clave: 'ph', etiqueta: 'pH' },
  { clave: 'pco2', etiqueta: 'PCO2' },
  { clave: 'po2', etiqueta: 'PO2' },
  { clave: 'hco3', etiqueta: 'HCO3' },
  { clave: 'excesoBase', etiqueta: 'Exceso de base' },
  { clave: 'fio2', etiqueta: 'FiO2', unidad: '%' },
  { clave: 'pao2Fio2', etiqueta: 'PaO2/FiO2' },
  { clave: 'sao2', etiqueta: 'SaO2' },
  { clave: 'lactato', etiqueta: 'Lactato' },
  { clave: 'na', etiqueta: 'Na' },
  { clave: 'k', etiqueta: 'K' },
  { clave: 'glucosa', etiqueta: 'Glucosa' },
];

// ---------- C. Bitácora — resultados de laboratorio ----------

export const CAMPOS_LABORATORIO_113 = [
  'leucocitos', 'jovenes', 'cayados', 'segmentados', 'eosinofilos', 'linfocitos', 'tp', 'ttp', 'plaquetas', 'tgo', 'tgp', 'hematocrito', 'glucosa', 'urea', 'creatinina', 'cl', 'na', 'k',
] as const;
export type CampoLaboratorio113 = (typeof CAMPOS_LABORATORIO_113)[number];

export type EstadoLaboratorio113 = Readonly<Record<CampoLaboratorio113, string>>;

export function estadoInicialLaboratorio113(): EstadoLaboratorio113 {
  return Object.fromEntries(CAMPOS_LABORATORIO_113.map((c) => [c, ''])) as EstadoLaboratorio113;
}

function paresDeLaboratorio113(t: Traducir, e: EstadoLaboratorio113): Par[] {
  return CAMPOS_LABORATORIO_113.map((c) => ({ pregunta: t(`formularios.f113.laboratorio.${c}`), respuesta: e[c].trim() }));
}

// ---------- C. Bitácora — balance hídrico (calculado; editable solo si se sobrescribe) ----------

export const CAMPOS_BALANCE_HIDRICO_113 = ['total1', 'total2', 'total3', 'total4', 'sumaIngresos', 'total5', 'total6', 'sumaEgresos', 'balanceDiario', 'balanceTotal'] as const;
export type CampoBalanceHidrico113 = (typeof CAMPOS_BALANCE_HIDRICO_113)[number];

/** Lo que la enfermera SOBRESCRIBIÓ a mano (`''` = usar el cálculo). Misma forma que antes: lo ya guardado sigue siendo un valor manual válido. */
export type EstadoBalanceHidrico113 = Readonly<Record<CampoBalanceHidrico113, string>>;

export function estadoInicialBalanceHidrico113(): EstadoBalanceHidrico113 {
  return Object.fromEntries(CAMPOS_BALANCE_HIDRICO_113.map((c) => [c, ''])) as EstadoBalanceHidrico113;
}

/**
 * Qué filas horarias componen cada TOTAL del papel (pág. 220 del PDF: el número de cada grupo — 1 agua endógena, 2 medicación, 3 infusiones,
 * 4 dieta, 5 egresos varios, 6 diuresis — coincide con el TOTAL de la columna derecha; el instructivo, pág. 224, solo nombra los balances
 * "agua endógena, ingresos, egresos, balance diario, balance total" sin fórmula). Decisiones, todas para no sumar dos veces ni sumar lo que
 * no es volumen:
 * - TOTAL 3 = infusiones + líquidos vía oral (el papel pone "Líquidos vía oral" dentro del bloque 3).
 * - TOTAL 4 = solo la fila horaria de la dieta: la fila "total" de la dieta es un acumulado y sumarla duplicaría el volumen.
 * - TOTAL 5 no incluye "Heces": el catálogo no le da unidad (mL) y puede ser un conteo o una descripción.
 * - TOTAL 6 = solo diuresis horaria (mismo motivo con la diuresis total); la irrigación (entrada/salida) no entra a ninguna suma:
 *   el papel no dice si resta y el instructivo solo la menciona para registrar la diuresis. Sigue registrándose en su fila.
 * - SUMA ingresos = TOTAL 1+2+3+4; SUMA egresos = TOTAL 5+6; BALANCE DIARIO = suma de ingresos − suma de egresos (solo con ambas sumas).
 */
export const CONFIG_BALANCE_HIDRICO_113: ConfigBalanceHidrico<CampoBalanceHidrico113> = {
  totalesIngresos: {
    total1: ['aguaEndogena'],
    total2: ['medicacion'],
    total3: ['infusiones', 'liquidosViaOral'],
    total4: ['dietaHoraria'],
  },
  totalesEgresos: {
    total5: ['insensibles', 'vomito', 'sondaNasogastrica', 'drenajes'],
    total6: ['diuresisHoraria'],
  },
  sumaIngresos: 'sumaIngresos',
  sumaEgresos: 'sumaEgresos',
  balanceDiario: 'balanceDiario',
};

/** Campos del balance que salen del cálculo (todos menos el BALANCE TOTAL acumulado, que solo se puede escribir a mano). */
export const CAMPOS_BALANCE_CALCULABLES_113: readonly CampoBalanceHidrico113[] = CAMPOS_BALANCE_HIDRICO_113.filter((c) => c !== 'balanceTotal');

/** Valor que se ve e imprime de cada campo: lo sobrescrito a mano o, si no, el calculado desde ingresos/egresos. Pura, exportada para test. */
export function balanceHidricoEfectivo113(manual: EstadoBalanceHidrico113, ingresos: EstadoTablaHoraria24h, egresos: EstadoTablaHoraria24h): EstadoBalanceHidrico113 {
  return balanceHidricoEfectivo(CONFIG_BALANCE_HIDRICO_113, CAMPOS_BALANCE_HIDRICO_113, manual, ingresos, egresos);
}

function paresDeBalanceHidrico113(t: Traducir, e: EstadoBalanceHidrico113): Par[] {
  return CAMPOS_BALANCE_HIDRICO_113.map((c) => ({ pregunta: t(`formularios.f113.balanceHidrico.${c}`), respuesta: e[c].trim() }));
}

// ---------- C. Bitácora — personal de enfermería responsable ----------

export interface EstadoPersonalEnfermeria113 {
  readonly am: string;
  readonly pm: string;
  readonly hs: string;
}

export function estadoInicialPersonalEnfermeria113(): EstadoPersonalEnfermeria113 {
  return { am: '', pm: '', hs: '' };
}

function paresDePersonalEnfermeria113(t: Traducir, e: EstadoPersonalEnfermeria113): Par[] {
  return [
    { pregunta: t('formularios.f113.personalEnfermeria.am'), respuesta: e.am.trim() },
    { pregunta: t('formularios.f113.personalEnfermeria.pm'), respuesta: e.pm.trim() },
    { pregunta: t('formularios.f113.personalEnfermeria.hs'), respuesta: e.hs.trim() },
  ];
}

// ---------- D. Ventilación mecánica (Tanda 8a — ver PLAN.md) ----------
// Deferida desde la Tanda 5b junto con E-K, ver el comentario de alcance en
// la cabecera del archivo. Leída de la página 221 del PDF fuente (20 filas
// numeradas, 21-24 en blanco en el papel — no se transcriben).

export const FILAS_VENTILACION_113: readonly FilaTablaHoraria24h[] = [
  { clave: 'modoVentilatorio', etiqueta: 'Modo ventilatorio' },
  { clave: 'presionPico', etiqueta: 'Presión pico' },
  { clave: 'presionMedia', etiqueta: 'Presión media' },
  { clave: 'peep', etiqueta: 'PEEP' },
  { clave: 'autoPeep', etiqueta: 'Auto PEEP' },
  { clave: 'presionSoporte', etiqueta: 'Presión soporte' },
  { clave: 'volumenTidalEspiradoMaquina', etiqueta: 'Volumen tidal espirado máquina' },
  { clave: 'volumenTidalEspiradoPaciente', etiqueta: 'Volumen tidal espirado paciente' },
  { clave: 'volumenMinutoEspiradoMaquina', etiqueta: 'Volumen minuto espirado máquina' },
  { clave: 'volumenMinutoEspiradoPaciente', etiqueta: 'Volumen minuto espirado paciente' },
  { clave: 'frecuenciaRespiratoriaMaquina', etiqueta: 'Frecuencia respiratoria máquina' },
  { clave: 'frecuenciaRespiratoriaEspontanea', etiqueta: 'Frecuencia respiratoria espontánea' },
  { clave: 'complianceEstatica', etiqueta: 'Compliance estática' },
  { clave: 'resistenciaInspiratoria', etiqueta: 'Resistencia inspiratoria' },
  { clave: 'fio2', etiqueta: 'FiO2' },
  { clave: 'etco2', etiqueta: 'EtCO2' },
  { clave: 'volumenFugas', etiqueta: 'Volumen fugas' },
  { clave: 'nivelTuboOrotraqueal', etiqueta: 'Nivel tubo orotraqueal' },
  { clave: 'presionBalonTuboOrotraqueal', etiqueta: 'Presión de balón tubo orotraqueal' },
  { clave: 'relacionInspiracionEspiracion', etiqueta: 'Relación inspiración / espiración' },
];

// ---------- E. Procedimientos (Tanda 8a) ----------
// Tabla fija (Trae/Inicio/Cambio/Retiro), no horaria — ver `TablaConColumnas`.

export const FILAS_PROCEDIMIENTOS_113: readonly FilaTablaConColumnas[] = [
  { clave: 'intubacionTraqueal', etiqueta: 'Intubación traqueal' },
  { clave: 'traqueostomia', etiqueta: 'Traqueostomía' },
  { clave: 'tuboToracico', etiqueta: 'Tubo torácico' },
  { clave: 'sondaGastrica', etiqueta: 'Sonda gástrica' },
  { clave: 'cateterDialisis', etiqueta: 'Catéter de diálisis' },
  { clave: 'cateterVenosoCentral', etiqueta: 'Catéter venoso central' },
  { clave: 'cateterVenosoPeriferico', etiqueta: 'Catéter venoso periférico' },
  { clave: 'marcapasosTemporal', etiqueta: 'Marcapasos temporal' },
  { clave: 'lineaArterial', etiqueta: 'Línea arterial' },
  { clave: 'sondaVesical', etiqueta: 'Sonda vesical' },
  { clave: 'puncionLumbar', etiqueta: 'Punción lumbar' },
];

/**
 * "Otros:" del instructivo (pág. 225) + los renglones en blanco del papel (3, contados en la pág. 221 a 300 dpi): el nombre del procedimiento se
 * escribe en la primera columna. Contra el papel, F. Oxigenación (113) NO trae renglones en blanco (solo aire ambiente, catéter nasal, mascarilla
 * y tubo en T), así que no lleva "otros".
 */
export const FILAS_PROCEDIMIENTOS_OTROS_113: readonly FilaTablaConColumnas[] = [
  { clave: 'otro1', etiqueta: '1' },
  { clave: 'otro2', etiqueta: '2' },
  { clave: 'otro3', etiqueta: '3' },
];

export const COLUMNAS_PROCEDIMIENTOS_113: readonly ColumnaTablaConColumnas[] = [
  { clave: 'trae', etiqueta: 'Trae', tipo: 'fecha' },
  { clave: 'inicio', etiqueta: 'Inicio', tipo: 'fecha' },
  { clave: 'cambio', etiqueta: 'Cambio', tipo: 'fecha' },
  { clave: 'retiro', etiqueta: 'Retiro', tipo: 'fecha' },
];

export const COLUMNAS_PROCEDIMIENTOS_OTROS_113: readonly ColumnaTablaConColumnas[] = [{ clave: 'nombre', etiqueta: 'Otro procedimiento' }, ...COLUMNAS_PROCEDIMIENTOS_113];

// ---------- F. Oxigenación (Tanda 8a) ----------
// Checklist frecuencia+AM/PM/HS — ver `ChecklistTurno`.

export const ITEMS_OXIGENACION_113: readonly ItemChecklistTurno[] = [
  { clave: 'aireAmbiente', etiqueta: 'Aire ambiente' },
  { clave: 'cateterNasal', etiqueta: 'Catéter nasal' },
  { clave: 'mascarilla', etiqueta: 'Mascarilla' },
  { clave: 'tuboEnT', etiqueta: 'Tubo en T' },
];

// ---------- G. Solicitud de exámenes (Tanda 8a) ----------
// El papel deja un número fijo de filas en blanco para completar a mano —
// mismo criterio de simplificación que "medicación"/"ingresos" del resto
// del motor (no una lista dinámica).

export const NUM_FILAS_SOLICITUD_EXAMENES_113 = 10; // el papel trae ~10 renglones (antes 5)

export const FILAS_SOLICITUD_EXAMENES_113: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_SOLICITUD_EXAMENES_113 }, (_, i) => ({
  clave: `examen${i + 1}`,
  etiqueta: String(i + 1),
}));

export const COLUMNAS_SOLICITUD_EXAMENES_113: readonly ColumnaTablaConColumnas[] = [
  { clave: 'nombre', etiqueta: 'Nombre' },
  { clave: 'fechaSolicitud', etiqueta: 'Fecha de solicitud', tipo: 'fecha' },
  { clave: 'horaToma', etiqueta: 'Hora de toma' },
  { clave: 'horaResultado', etiqueta: 'Hora de resultado' },
];

// ---------- H. Cuidados generales (Tanda 8a) ----------
// Checklist frecuencia+AM/PM/HS — 24 filas leídas de la página 221 (25-26
// en blanco en el papel, no se transcriben).

export const ITEMS_CUIDADOS_GENERALES_113: readonly ItemChecklistTurno[] = [
  { clave: 'aislamiento', etiqueta: 'Aislamiento' },
  { clave: 'bano', etiqueta: 'Baño' },
  { clave: 'aseoPerineal', etiqueta: 'Aseo perineal' },
  { clave: 'reposoRelativo', etiqueta: 'Reposo relativo' },
  { clave: 'posicion', etiqueta: 'Posición' },
  { clave: 'cambiosPosicion', etiqueta: 'Cambios de posición' },
  { clave: 'levantarlo', etiqueta: 'Levantarlo' },
  { clave: 'cuidadosPiel', etiqueta: 'Cuidados de piel' },
  { clave: 'cuidadosOjos', etiqueta: 'Cuidados de ojos' },
  { clave: 'higieneBucal', etiqueta: 'Higiene bucal' },
  { clave: 'controlFiebreMediosFisicos', etiqueta: 'Control fiebre medios físicos' },
  { clave: 'rehabilitacionMotora', etiqueta: 'Rehabilitación motora' },
  { clave: 'nebulizaciones', etiqueta: 'Nebulizaciones' },
  { clave: 'terapiaRespiratoria', etiqueta: 'Terapia respiratoria' },
  { clave: 'cuidadosTuboTraqueal', etiqueta: 'Cuidados de tubo traqueal' },
  { clave: 'aspiracionSecreciones', etiqueta: 'Aspiración de secreciones' },
  { clave: 'curacion', etiqueta: 'Curación' },
  { clave: 'controlSangrado', etiqueta: 'Control de sangrado' },
  { clave: 'cuidadoDrenajes', etiqueta: 'Cuidado de drenajes' },
  { clave: 'controlSondaGastrica', etiqueta: 'Control de sonda gástrica' },
  { clave: 'controlSondaVesical', etiqueta: 'Control de sonda vesical' },
  { clave: 'riesgoCaidas', etiqueta: 'Riesgo caídas' },
  { clave: 'riesgoUlcerasPresion', etiqueta: 'Riesgo úlceras de presión' },
  { clave: 'riesgoTromboembolismo', etiqueta: 'Riesgo de tromboembolismo' },
  // Renglones 25-26: en blanco en el papel; el nombre lo escribe la enfermera.
  { clave: 'otro1', etiqueta: 'Otro cuidado', nombreEditable: true },
  { clave: 'otro2', etiqueta: 'Otro cuidado', nombreEditable: true },
];

// ---------- I. Interconsultas (Tanda 8a) ----------

export const NUM_FILAS_INTERCONSULTAS_113 = 6; // el papel trae ~6 renglones (antes 3)

export const FILAS_INTERCONSULTAS_113: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_INTERCONSULTAS_113 }, (_, i) => ({
  clave: `interconsulta${i + 1}`,
  etiqueta: String(i + 1),
}));

export const COLUMNAS_INTERCONSULTAS_113: readonly ColumnaTablaConColumnas[] = [
  { clave: 'servicio', etiqueta: 'Servicio interconsultado' },
  { clave: 'horaSolicitud', etiqueta: 'Hora de solicitud' },
  { clave: 'horaRespuesta', etiqueta: 'Hora de respuesta' },
];

// ---------- J. Informe de enfermería (Tanda 8a) ----------
// K (Datos del profesional responsable por turno) vive en `profesionalPorTurnoDatos.ts` (T6): ya no se omite. "Personal de enfermería
// responsable" (Tanda 5b) se conserva aparte.

export interface EstadoInformeEnfermeria113 {
  readonly am: string;
  readonly pm: string;
  readonly hs: string;
}

export function estadoInicialInformeEnfermeria113(): EstadoInformeEnfermeria113 {
  return { am: '', pm: '', hs: '' };
}

function paresDeInformeEnfermeria113(t: Traducir, e: EstadoInformeEnfermeria113): Par[] {
  return [
    { pregunta: t('formularios.f113.informeEnfermeria.am'), respuesta: e.am.trim() },
    { pregunta: t('formularios.f113.informeEnfermeria.pm'), respuesta: e.pm.trim() },
    { pregunta: t('formularios.f113.informeEnfermeria.hs'), respuesta: e.hs.trim() },
  ];
}

// ---------- Estado combinado (Bloque C completo) ----------

export interface EstadoBitacora113 {
  readonly antropometria: EstadoAntropometria113;
  readonly datosBitacora: EstadoDatosBitacora113;
  readonly diagnosticos: readonly DiagnosticoCie10Valor[];
  readonly constantesVitales: EstadoTablaHoraria24h;
  readonly graficoConstantes: PuntosGraficoEnfermeria;
  readonly ingresos: EstadoTablaHoraria24h;
  readonly egresos: EstadoTablaHoraria24h;
  readonly reflejos: EstadoTablaHoraria24h;
  readonly escalas: EstadoTablaHoraria24h;
  readonly gases: EstadoTablaHoraria24h;
  readonly laboratorio: EstadoLaboratorio113;
  readonly balanceHidrico: EstadoBalanceHidrico113;
  readonly personalEnfermeria: EstadoPersonalEnfermeria113;
  readonly ventilacion: EstadoTablaHoraria24h;
  readonly procedimientos: EstadoTablaConColumnas;
  readonly oxigenacion: EstadoChecklistTurno;
  readonly solicitudExamenes: EstadoTablaConColumnas;
  readonly cuidadosGenerales: EstadoChecklistTurno;
  readonly interconsultas: EstadoTablaConColumnas;
  readonly informeEnfermeria: EstadoInformeEnfermeria113;
  readonly profesionalTurnos: EstadoProfesionalPorTurno;
  readonly grupos: GruposNombrables113;
  readonly procedimientosOtros: EstadoTablaConColumnas;
}

export function estadoInicialBitacora113(): EstadoBitacora113 {
  return {
    antropometria: estadoInicialAntropometria113(),
    datosBitacora: estadoInicialDatosBitacora113(),
    diagnosticos: estadoInicialDiagnosticos113(),
    constantesVitales: estadoInicialTablaHoraria24h(FILAS_CONSTANTES_VITALES_113),
    graficoConstantes: estadoInicialGraficoEnfermeria(SERIES_GRAFICO_CONSTANTES_113),
    ingresos: estadoInicialTablaHoraria24h(FILAS_INGRESOS_113),
    egresos: estadoInicialTablaHoraria24h(FILAS_EGRESOS_113),
    reflejos: estadoInicialTablaHoraria24h(FILAS_REFLEJOS_113),
    escalas: estadoInicialTablaHoraria24h(FILAS_ESCALAS_113),
    gases: estadoInicialTablaHoraria24h(FILAS_GASES_113),
    laboratorio: estadoInicialLaboratorio113(),
    balanceHidrico: estadoInicialBalanceHidrico113(),
    personalEnfermeria: estadoInicialPersonalEnfermeria113(),
    ventilacion: estadoInicialTablaHoraria24h(FILAS_VENTILACION_113),
    procedimientos: estadoInicialTablaConColumnas(FILAS_PROCEDIMIENTOS_113, COLUMNAS_PROCEDIMIENTOS_113),
    oxigenacion: estadoInicialChecklistTurno(ITEMS_OXIGENACION_113),
    solicitudExamenes: estadoInicialTablaConColumnas(FILAS_SOLICITUD_EXAMENES_113, COLUMNAS_SOLICITUD_EXAMENES_113),
    cuidadosGenerales: estadoInicialChecklistTurno(ITEMS_CUIDADOS_GENERALES_113),
    interconsultas: estadoInicialTablaConColumnas(FILAS_INTERCONSULTAS_113, COLUMNAS_INTERCONSULTAS_113),
    informeEnfermeria: estadoInicialInformeEnfermeria113(),
    profesionalTurnos: estadoInicialProfesionalPorTurno(),
    grupos: estadoInicialGruposNombrables113(),
    procedimientosOtros: estadoInicialTablaConColumnas(FILAS_PROCEDIMIENTOS_OTROS_113, COLUMNAS_PROCEDIMIENTOS_OTROS_113),
  };
}

/** Tablas de ingresos y egresos con los renglones nombrables ya sumados (filas sintéticas `medicacion`/`infusiones`/`drenajes`) — lo que lee el balance. */
export function tablasBalance113(estado: Pick<EstadoBitacora113, 'ingresos' | 'egresos' | 'grupos'>): { ingresos: EstadoTablaHoraria24h; egresos: EstadoTablaHoraria24h } {
  return {
    ingresos: tablaConGruposNombrables(estado.ingresos, { medicacion: estado.grupos.medicacion, infusiones: estado.grupos.infusiones }),
    egresos: tablaConGruposNombrables(estado.egresos, { drenajes: estado.grupos.drenajes }),
  };
}

function paresDeFilasHorarias(filas: readonly FilaTablaHoraria24h[], estado: EstadoTablaHoraria24h): Par[] {
  return filas
    .map((f) => ({ pregunta: f.etiqueta, respuesta: resumenFilaTablaHoraria24h(estado, f.clave) }))
    .filter((p) => p.respuesta !== '');
}

function paresDeFilasConColumnas(filas: readonly FilaTablaConColumnas[], columnas: readonly ColumnaTablaConColumnas[], estado: EstadoTablaConColumnas, prefijoEtiqueta: string): Par[] {
  return filas
    .map((f) => ({ pregunta: `${prefijoEtiqueta} ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(estado, f.clave, columnas) }))
    .filter((p) => p.respuesta !== '');
}


/** "Otros" procedimientos: "<Procedimientos> — <nombre>" con sus fechas; un renglón sin nombre sale numerado. */
function paresDeProcedimientosOtros113(t: Traducir, estado: EstadoTablaConColumnas): Par[] {
  const titulo = t('formularios.f113.procedimientosTitulo');
  return FILAS_PROCEDIMIENTOS_OTROS_113.flatMap((f) => {
    const nombre = (estado[f.clave]?.nombre ?? '').trim();
    const fechas = resumenFilaTablaConColumnas(estado, f.clave, COLUMNAS_PROCEDIMIENTOS_113);
    if (nombre === '' && fechas === '') return [];
    return [{ pregunta: `${titulo} — ${nombre !== '' ? nombre : `${t('formularios.uci.textosFijos.otroRenglon')} ${f.etiqueta}`}`, respuesta: fechas || t('formularios.uci.textosFijos.sinFechas') }];
  });
}

/** Serializa todo el Bloque C a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeBitacora113(t: Traducir, estado: EstadoBitacora113): Par[] {
  const resumenGlasgow = resumenTotalGlasgowPorHora(estado.escalas);
  const { ingresos, egresos } = tablasBalance113(estado);
  const g = estado.grupos;
  const pares: Par[] = [
    ...paresDeAntropometria113(t, estado.antropometria),
    ...paresDeDatosBitacora113(t, estado.datosBitacora),
    ...paresDeDiagnosticos113(t, estado.diagnosticos),
    ...paresDeFilasHorarias(FILAS_CONSTANTES_VITALES_113, estado.constantesVitales),
    ...paresDeFilasNombrables(t('formularios.f113.otrasConstantesTitulo'), g.otrasConstantes, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasHorarias(FILAS_INGRESOS_113, estado.ingresos),
    ...paresDeFilasNombrables(t('formularios.f113.medicacionTitulo'), g.medicacion, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasNombrables(t('formularios.f113.infusionesTitulo'), g.infusiones, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasHorarias(FILAS_EGRESOS_113, estado.egresos),
    ...paresDeFilasNombrables(t('formularios.f113.drenajesTitulo'), g.drenajes, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasHorarias(FILAS_REFLEJOS_113, estado.reflejos),
    ...paresDeFilasHorarias(FILAS_ESCALAS_113, estado.escalas),
    ...(resumenGlasgow !== '' ? [{ pregunta: t('formularios.f113.escalas.totalGlasgow'), respuesta: resumenGlasgow }] : []),
    ...paresDeFilasHorarias(FILAS_GASES_113, estado.gases),
    ...paresDeLaboratorio113(t, estado.laboratorio),
    ...paresDeBalanceHidrico113(t, balanceHidricoEfectivo113(estado.balanceHidrico, ingresos, egresos)),
    ...paresDePersonalEnfermeria113(t, estado.personalEnfermeria),
    ...paresDeFilasHorarias(FILAS_VENTILACION_113, estado.ventilacion),
    ...paresDeFilasNombrables(t('formularios.f113.otrosVentilacionTitulo'), g.otrosVentilacion, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasConColumnas(FILAS_PROCEDIMIENTOS_113, COLUMNAS_PROCEDIMIENTOS_113, estado.procedimientos, t('formularios.f113.procedimientosTitulo')),
    ...paresDeProcedimientosOtros113(t, estado.procedimientosOtros),
    ...paresDeItemsChecklistTurno(ITEMS_OXIGENACION_113, estado.oxigenacion),
    ...paresDeFilasConColumnas(FILAS_SOLICITUD_EXAMENES_113, COLUMNAS_SOLICITUD_EXAMENES_113, estado.solicitudExamenes, t('formularios.f113.solicitudExamenesTitulo')),
    ...paresDeItemsChecklistTurno(ITEMS_CUIDADOS_GENERALES_113, estado.cuidadosGenerales),
    ...paresDeFilasConColumnas(FILAS_INTERCONSULTAS_113, COLUMNAS_INTERCONSULTAS_113, estado.interconsultas, t('formularios.f113.interconsultasTitulo')),
    ...paresDeInformeEnfermeria113(t, estado.informeEnfermeria),
    ...paresProfesionalPorTurno(t, 'formularios.f113.profesionalTurnos', CAMPOS_PROFESIONAL_113, estado.profesionalTurnos),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
